import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import {
  defaultContent,
  LOCAL_CONTENT_STORAGE_KEY,
  normaliseContent,
  type EditableBikeModelContent,
  type EditableBuilderSettings,
  type EditableBuilderPart,
  type EditableBuilderPartAddition,
  type EditableFinishAddition,
  type EditableFinishOption,
  type EditableHomeContent,
  type EditableLegalDoc,
  type EditableSiteContent,
  type EditableSiteSettings,
} from "./defaultContent";
import publishedContentJson from "./publishedContent.json";
import { getStoredContent, removeStoredContent, setStoredContent } from "./idb";
import type { BuilderAddon, BuilderStiffnessOption, FrameColourOption, FrameLogo } from "@/types";

/** Live persistence state for the local CMS, surfaced to the workbench UI. */
export type SaveStatus = "saved" | "draft" | "saving" | "error";
export type ContentSource = "published" | "local-draft";

const PUBLISH_ENDPOINT = "/__burton-local-content/publish";

interface ContentContextValue {
  content: EditableSiteContent;
  /** Whether the latest edits have been written to the repo content file. */
  saveStatus: SaveStatus;
  /** Human-readable explanation shown when saveStatus is 'error'. */
  saveError: string | null;
  /** Whether the app is showing the committed content file or a one-time migrated browser draft. */
  contentSource: ContentSource;
  updateHome: (patch: Partial<EditableHomeContent>) => void;
  updateBikeModel: (
    modelId: string,
    patch: Partial<EditableBikeModelContent>,
  ) => void;
  updateSiteSettings: (patch: Partial<EditableSiteSettings>) => void;
  updateBuilderSettings: (patch: Partial<EditableBuilderSettings>) => void;
  updateBuilderPart: (
    partId: string,
    patch: Partial<EditableBuilderPart>,
  ) => void;
  addBuilderPart: (part: EditableBuilderPartAddition) => void;
  updateBuilderPartAddition: (
    partId: string,
    patch: Partial<EditableBuilderPartAddition>,
  ) => void;
  removeBuilderPartAddition: (partId: string) => void;
  /** Soft-delete a catalog/imported part from the builder (reversible). */
  removeBuilderPart: (partId: string) => void;
  /** Restore a previously soft-deleted catalog/imported part. */
  restoreBuilderPart: (partId: string) => void;
  updateFinishOption: (
    finishId: string,
    patch: Partial<EditableFinishOption>,
  ) => void;
  addFinishAddition: (finish: EditableFinishAddition) => void;
  updateFinishAddition: (
    finishId: string,
    patch: Partial<EditableFinishAddition>,
  ) => void;
  removeFinishAddition: (finishId: string) => void;
  updateFrameLogos: (logos: FrameLogo[]) => void;
  updateLogoColourOptions: (colours: FrameColourOption[]) => void;
  updateCarbonTubeTintColours: (colours: FrameColourOption[]) => void;
  updateStiffnessOptions: (options: BuilderStiffnessOption[]) => void;
  updateBuilderAddons: (addons: BuilderAddon[]) => void;
  updateLayerOrder: (order: string[]) => void;
  /** Patch a single legal / compliance page by slug. */
  updateLegalDoc: (slug: string, patch: Partial<EditableLegalDoc>) => void;
  replaceContent: (content: EditableSiteContent) => void;
  publishContent: () => Promise<void>;
  resetContent: () => void;
}

const ContentContext = createContext<ContentContextValue | null>(null);
const publishedContent = normaliseContent(
  publishedContentJson as Partial<EditableSiteContent>,
);
const publishedContentSerialized = serializeContent(publishedContent);

function serializeContent(content: EditableSiteContent) {
  return JSON.stringify(content);
}

async function readInitialContent(): Promise<{
  content: EditableSiteContent;
  source: ContentSource;
}> {
  if (typeof window === "undefined") {
    return { content: publishedContent, source: "published" };
  }
  if (!import.meta.env.DEV) {
    return { content: publishedContent, source: "published" };
  }

  try {
    let stored = await getStoredContent();
    if (!stored) {
      stored = readLegacyStoredContent();
    }
    if (!stored) return { content: publishedContent, source: "published" };
    const storedContent = normaliseContent(
      JSON.parse(stored) as Partial<EditableSiteContent>,
    );
    if (serializeContent(storedContent) === publishedContentSerialized) {
      return { content: publishedContent, source: "published" };
    }
    return {
      content: storedContent,
      source: "local-draft",
    };
  } catch {
    return { content: publishedContent, source: "published" };
  }
}

function readLegacyStoredContent() {
  try {
    return window.localStorage.getItem(LOCAL_CONTENT_STORAGE_KEY);
  } catch {
    return null;
  }
}

function writeLegacyStoredContent(serialized: string) {
  try {
    window.localStorage.setItem(LOCAL_CONTENT_STORAGE_KEY, serialized);
    return true;
  } catch {
    return false;
  }
}

function removeLegacyStoredContent() {
  try {
    window.localStorage.removeItem(LOCAL_CONTENT_STORAGE_KEY);
  } catch {
    // A blocked localStorage cleanup should not keep a saved repo file in draft mode.
  }
}

async function publishContentToRepoFile(content: EditableSiteContent) {
  const response = await fetch(PUBLISH_ENDPOINT, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(content),
  });

  if (!response.ok) {
    const details = await response.text().catch(() => "");
    throw new Error(details || `Publish failed with ${response.status}`);
  }
}

function publishContentBeforeUnload(content: EditableSiteContent) {
  if (typeof window === "undefined") return;
  const serialized = JSON.stringify(content);
  writeLegacyStoredContent(serialized);
  void setStoredContent(serialized).catch((error) => {
    console.error("Local content draft could not be flushed before unload.", error);
  });
}

async function clearLegacyStoredContent() {
  if (typeof window === "undefined") return;
  removeLegacyStoredContent();
  await removeStoredContent();
}

async function persistDraft(content: EditableSiteContent) {
  const serialized = serializeContent(content);
  const legacyStored = writeLegacyStoredContent(serialized);

  try {
    await setStoredContent(serialized);
    return true;
  } catch (error) {
    console.error("Local content draft could not be stored.", error);
    return legacyStored;
  }
}

const REPO_SAVE_MESSAGE =
  "Could not write src/content/publishedContent.json from this browser. " +
  "Make sure the site is running through npm.cmd run dev on localhost:3000, then try again.";

export function ContentProvider({ children }: { children: ReactNode }) {
  const [content, setContent] = useState<EditableSiteContent>(publishedContent);
  const [publishedSnapshot, setPublishedSnapshot] =
    useState<EditableSiteContent>(publishedContent);
  const [saveStatus, setSaveStatus] = useState<SaveStatus>("saved");
  const [saveError, setSaveError] = useState<string | null>(null);
  const [contentSource, setContentSource] = useState<ContentSource>("published");
  const [isHydrated, setIsHydrated] = useState(false);

  useEffect(() => {
    if (typeof window === "undefined" || !isHydrated) return;
    if (contentSource !== "local-draft") return;

    setSaveStatus("draft");
    setSaveError(null);

    void persistDraft(content).then((stored) => {
      if (!stored) {
        setSaveStatus("error");
        setSaveError(
          "Could not keep this draft in browser storage. Try pressing Save website changes now, or export a JSON backup.",
        );
      }
    });
  }, [content, contentSource, isHydrated]);

  // Full-page navigations can fire while draft persistence is still debouncing.
  useEffect(() => {
    if (typeof window === "undefined") return;
    const flush = () => {
      if (contentSource === "local-draft") publishContentBeforeUnload(content);
    };
    window.addEventListener("pagehide", flush);
    return () => window.removeEventListener("pagehide", flush);
  }, [content, contentSource]);

  useEffect(() => {
    if (typeof window === "undefined") return;

    let isCurrent = true;

    void (async () => {
      const initialContent = await readInitialContent();
      if (!isCurrent) return;
      setContent(initialContent.content);
      setContentSource(initialContent.source);
      setSaveStatus(initialContent.source === "local-draft" ? "draft" : "saved");
      setIsHydrated(true);
    })();

    return () => {
      isCurrent = false;
    };
  }, []);

  const markDraft = useCallback(() => {
    setContentSource("local-draft");
  }, []);

  const updateHome = useCallback((patch: Partial<EditableHomeContent>) => {
    markDraft();
    setContent((current) => ({
      ...current,
      home: { ...current.home, ...patch },
    }));
  }, [markDraft]);

  const updateBikeModel = useCallback(
    (modelId: string, patch: Partial<EditableBikeModelContent>) => {
      markDraft();
      setContent((current) => ({
        ...current,
        bikeModels: {
          ...current.bikeModels,
          [modelId]: {
            ...(current.bikeModels[modelId] ??
              defaultContent.bikeModels["raptor-i"]!),
            ...patch,
          },
        },
      }));
    },
    [markDraft],
  );

  const updateSiteSettings = useCallback(
    (patch: Partial<EditableSiteSettings>) => {
      markDraft();
      setContent((current) => ({
        ...current,
        siteSettings: { ...current.siteSettings, ...patch },
      }));
    },
    [markDraft],
  );

  const updateBuilderSettings = useCallback(
    (patch: Partial<EditableBuilderSettings>) => {
      markDraft();
      setContent((current) => ({
        ...current,
        builderSettings: { ...current.builderSettings, ...patch },
      }));
    },
    [markDraft],
  );

  const updateBuilderPart = useCallback(
    (partId: string, patch: Partial<EditableBuilderPart>) => {
      markDraft();
      setContent((current) => ({
        ...current,
        builderPartOverrides: {
          ...current.builderPartOverrides,
          [partId]: {
            ...(current.builderPartOverrides[partId] ?? {}),
            ...patch,
          },
        },
      }));
    },
    [markDraft],
  );

  const addBuilderPart = useCallback((part: EditableBuilderPartAddition) => {
    markDraft();
    setContent((current) => ({
      ...current,
      builderPartAdditions: [...current.builderPartAdditions, part],
    }));
  }, [markDraft]);

  const updateBuilderPartAddition = useCallback(
    (partId: string, patch: Partial<EditableBuilderPartAddition>) => {
      markDraft();
      setContent((current) => ({
        ...current,
        builderPartAdditions: current.builderPartAdditions.map((part) =>
          part.id === partId ? { ...part, ...patch } : part,
        ),
      }));
    },
    [markDraft],
  );

  const removeBuilderPartAddition = useCallback((partId: string) => {
    markDraft();
    setContent((current) => ({
      ...current,
      builderPartAdditions: current.builderPartAdditions.filter(
        (part) => part.id !== partId,
      ),
    }));
  }, [markDraft]);

  const removeBuilderPart = useCallback((partId: string) => {
    markDraft();
    setContent((current) =>
      current.removedBuilderPartIds.includes(partId)
        ? current
        : {
            ...current,
            removedBuilderPartIds: [...current.removedBuilderPartIds, partId],
          },
    );
  }, [markDraft]);

  const restoreBuilderPart = useCallback((partId: string) => {
    markDraft();
    setContent((current) => ({
      ...current,
      removedBuilderPartIds: current.removedBuilderPartIds.filter(
        (id) => id !== partId,
      ),
    }));
  }, [markDraft]);

  const updateFinishOption = useCallback(
    (finishId: string, patch: Partial<EditableFinishOption>) => {
      markDraft();
      setContent((current) => ({
        ...current,
        finishOverrides: {
          ...current.finishOverrides,
          [finishId]: {
            ...(current.finishOverrides[finishId] ?? {}),
            ...patch,
          },
        },
      }));
    },
    [markDraft],
  );

  const addFinishAddition = useCallback((finish: EditableFinishAddition) => {
    markDraft();
    setContent((current) => ({
      ...current,
      finishAdditions: [...current.finishAdditions, finish],
    }));
  }, [markDraft]);

  const updateFinishAddition = useCallback(
    (finishId: string, patch: Partial<EditableFinishAddition>) => {
      markDraft();
      setContent((current) => ({
        ...current,
        finishAdditions: current.finishAdditions.map((finish) =>
          finish.id === finishId ? { ...finish, ...patch } : finish,
        ),
      }));
    },
    [markDraft],
  );

  const removeFinishAddition = useCallback((finishId: string) => {
    markDraft();
    setContent((current) => ({
      ...current,
      finishAdditions: current.finishAdditions.filter(
        (finish) => finish.id !== finishId,
      ),
    }));
  }, [markDraft]);

  const updateFrameLogos = useCallback((logos: FrameLogo[]) => {
    markDraft();
    setContent((current) => ({ ...current, frameLogos: logos }));
  }, [markDraft]);

  const updateLogoColourOptions = useCallback(
    (colours: FrameColourOption[]) => {
      markDraft();
      setContent((current) => ({ ...current, logoColourOptions: colours }));
    },
    [markDraft],
  );

  const updateCarbonTubeTintColours = useCallback(
    (colours: FrameColourOption[]) => {
      markDraft();
      setContent((current) => ({ ...current, carbonTubeTintColours: colours }));
    },
    [markDraft],
  );

  const updateStiffnessOptions = useCallback(
    (options: BuilderStiffnessOption[]) => {
      markDraft();
      setContent((current) => ({ ...current, stiffnessOptions: options }));
    },
    [markDraft],
  );

  const updateBuilderAddons = useCallback((addons: BuilderAddon[]) => {
    markDraft();
    setContent((current) => ({ ...current, builderAddons: addons }));
  }, [markDraft]);

  const updateLayerOrder = useCallback((order: string[]) => {
    markDraft();
    setContent((current) => ({ ...current, layerOrder: order }));
  }, [markDraft]);

  const updateLegalDoc = useCallback(
    (slug: string, patch: Partial<EditableLegalDoc>) => {
      markDraft();
      setContent((current) => ({
        ...current,
        legalDocs: current.legalDocs.map((doc) =>
          doc.slug === slug ? { ...doc, ...patch } : doc,
        ),
      }));
    },
    [markDraft],
  );

  const replaceContent = useCallback((nextContent: EditableSiteContent) => {
    markDraft();
    setContent(normaliseContent(nextContent));
  }, [markDraft]);

  const publishContent = useCallback(async () => {
    setSaveStatus("saving");
    setSaveError(null);

    try {
      // Best-effort browser backup — a failing IndexedDB/localStorage must
      // never block writing the repo file, which is the real source of truth.
      await persistDraft(content);
      await publishContentToRepoFile(content);
      // The repo file now holds everything, so drop the browser draft — a
      // stale draft left behind would shadow the published file on reload.
      await clearLegacyStoredContent();
      setPublishedSnapshot(content);
      setContentSource("published");
      setSaveStatus("saved");
    } catch (error) {
      console.error("Local content could not be written to the repo.", error);
      setContentSource("local-draft");
      setSaveStatus("error");
      const details =
        error instanceof Error && error.message ? ` Details: ${error.message}` : "";
      setSaveError(`${REPO_SAVE_MESSAGE}${details}`);
      throw error;
    }
  }, [content]);

  const resetContent = useCallback(() => {
    setContent(publishedSnapshot);
    setContentSource("published");
    setSaveStatus("saved");
    setSaveError(null);
    void clearLegacyStoredContent();
  }, [publishedSnapshot]);

  const value = useMemo<ContentContextValue>(
    () => ({
      content,
      saveStatus,
      saveError,
      contentSource,
      updateHome,
      updateBikeModel,
      updateSiteSettings,
      updateBuilderSettings,
      updateBuilderPart,
      addBuilderPart,
      updateBuilderPartAddition,
      removeBuilderPartAddition,
      removeBuilderPart,
      restoreBuilderPart,
      updateFinishOption,
      addFinishAddition,
      updateFinishAddition,
      removeFinishAddition,
      updateFrameLogos,
      updateLogoColourOptions,
      updateCarbonTubeTintColours,
      updateStiffnessOptions,
      updateBuilderAddons,
      updateLayerOrder,
      updateLegalDoc,
      replaceContent,
      publishContent,
      resetContent,
    }),
    [
      content,
      saveStatus,
      saveError,
      contentSource,
      updateHome,
      updateBikeModel,
      updateSiteSettings,
      updateBuilderSettings,
      updateBuilderPart,
      addBuilderPart,
      updateBuilderPartAddition,
      removeBuilderPartAddition,
      removeBuilderPart,
      restoreBuilderPart,
      updateFinishOption,
      addFinishAddition,
      updateFinishAddition,
      removeFinishAddition,
      updateFrameLogos,
      updateLogoColourOptions,
      updateCarbonTubeTintColours,
      updateStiffnessOptions,
      updateBuilderAddons,
      updateLayerOrder,
      updateLegalDoc,
      replaceContent,
      publishContent,
      resetContent,
    ],
  );

  return (
    <ContentContext.Provider value={value}>{children}</ContentContext.Provider>
  );
}

export function useContent() {
  const context = useContext(ContentContext);
  if (!context) {
    throw new Error("useContent must be used within a ContentProvider");
  }
  return context;
}
